import { cn } from "./ui/cn";
import { useIsDesktop } from "../lib/media";
import type { Channel, ChannelType, Pipeline, User } from "../types";

/**
 * Filters that narrow whatever the current view returned.
 *
 * Deliberately additive to the view rather than replacing it: "Unassigned" plus
 * a pipeline filter is a question agents actually ask, and building it from two
 * controls beats maintaining a saved view for every combination.
 *
 * A row of controls and not a bar: it draws no border, no background and no
 * padding of its own, because it sits inline in the page header beside the
 * view's name. It was a full-width strip until it turned out that two selects
 * and the name of the view they narrow are one sentence and fit on one line --
 * and that the strip cost forty-five pixels above every conversation to hold
 * two dropdowns that narrow the list in the next column along.
 */
/**
 * Status is deliberately absent. Every view is already open-only except "All
 * closed", so a status filter could only offer the one value the view already
 * has -- a control that can never change the result.
 */
export interface Filters {
  pipeline?: number | "unassigned";
  assignee?: number | "unassigned";
  channel?: ChannelFilter;
}

/**
 * The kinds of channel the filter offers, in the order it offers them.
 *
 * Not every `ChannelType`. A web form ticket moves to Gmail the moment it is
 * answered -- see `#replyFromWebForm` -- so "web form" would be a list of the
 * unanswered ones, which is a question nobody asked; and SMS has no channel
 * behind it yet. Either joins by being added here.
 */
export const CHANNEL_FILTER_TYPES = ["gmail", "whatsapp", "ebay"] as const;
type FilterableChannelType = (typeof CHANNEL_FILTER_TYPES)[number];

/** One channel by id, or every channel of a kind by its type. */
export type ChannelFilter = number | FilterableChannelType;

const CHANNEL_TYPE_LABELS: Record<FilterableChannelType, string> = {
  gmail: "Email",
  whatsapp: "WhatsApp",
  ebay: "eBay",
};

function isFilterable(type: ChannelType): type is FilterableChannelType {
  return (CHANNEL_FILTER_TYPES as readonly string[]).includes(type);
}

/**
 * The channel dropdown's contents: a group per kind, opening with "All" of it
 * and then each account.
 *
 * A kind with one channel is a single option and no group. eBay has one
 * account, and "All eBay" above the only eBay account would be two ways of
 * asking the same thing -- and if a second mailbox or number is ever added,
 * the group appears on its own.
 *
 * Inactive channels stay listed. Their tickets did not go anywhere when the
 * channel was switched off, and finding them is exactly when you would reach
 * for this.
 */
function channelOptions(channels: Channel[]): SelectGroup[] {
  return CHANNEL_FILTER_TYPES.flatMap((type): SelectGroup[] => {
    const ofType = channels.filter(
      (channel) => isFilterable(channel.type) && channel.type === type,
    );
    if (ofType.length === 0) return [];

    const label = CHANNEL_TYPE_LABELS[type];
    if (ofType.length === 1) {
      return [{ options: [{ value: type, label }] }];
    }

    return [
      {
        label,
        options: [
          { value: type, label: `All ${type === "gmail" ? "email" : label}` },
          ...ofType.map((channel) => ({
            value: String(channel.id),
            label: channelLabel(channel),
          })),
        ],
      },
    ];
  });
}

/**
 * The address is what tells two mailboxes apart, so it is always there; the
 * display name is added only where it says something the address does not.
 */
function channelLabel(channel: Channel): string {
  const name = channel.displayName.trim();
  const base =
    name && name !== channel.address
      ? `${name} (${channel.address})`
      : channel.address;
  return channel.isActive ? base : `${base} (inactive)`;
}

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  pipelines: Pipeline[];
  users: User[];
  channels: Channel[];
  /** Hidden when the view itself already fixes the assignee. */
  showAssignee?: boolean;
  /**
   * True while a search is running, which widens the result past the view.
   *
   * The box itself lives in the header above -- it searches the whole desk
   * rather than narrowing this view, so it is not one of these -- but its
   * effect on the list is explained here, next to the list it changed.
   */
  searchSpansClosed?: boolean;
}

export function FilterBar({
  filters,
  onChange,
  pipelines,
  users,
  channels,
  showAssignee = true,
  searchSpansClosed = false,
}: FilterBarProps) {
  /*
   * Counted over what this view can show, not over what is set.
   *
   * Filters travel between views now, and a view that fixes the assignee
   * ignores one it was handed rather than applying it -- so counting the
   * hidden value would offer to clear a filter that is not narrowing anything,
   * and obscure the one that is. The button itself still clears the lot: a
   * filter with no control on screen is not something to leave somebody
   * carrying.
   */
  const active = [
    filters.pipeline,
    showAssignee ? filters.assignee : undefined,
    filters.channel,
  ].filter((value) => value !== undefined).length;

  /*
   * On a phone the selects share one line in equal thirds, and each names
   * itself in its empty option instead of in a label beside it. Labelled and
   * sized to their widest option, as on a desktop, they stacked into three
   * rows -- and one mailbox's "Name (address)" was enough to push the whole
   * queue some fifty pixels wider than the screen, because a native select is
   * as wide as its longest option however little of it is showing.
   */
  const isDesktop = useIsDesktop();

  return (
    <div className="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto">
      {/* `contents` on a desktop, so the row there is exactly what it was. */}
      <div className="flex w-full min-w-0 gap-1.5 md:contents">
      <Select
        compact={!isDesktop}
        label="Channel"
        value={filters.channel ?? ""}
        onChange={(raw) =>
          onChange({
            ...filters,
            channel:
              raw === ""
                ? undefined
                : (CHANNEL_FILTER_TYPES as readonly string[]).includes(raw)
                  ? (raw as FilterableChannelType)
                  : Number(raw),
          })
        }
        groups={channelOptions(channels)}
      />

      <Select
        compact={!isDesktop}
        label="Pipeline"
        value={filters.pipeline ?? ""}
        onChange={(raw) =>
          onChange({
            ...filters,
            pipeline:
              raw === ""
                ? undefined
                : raw === "unassigned"
                  ? "unassigned"
                  : Number(raw),
          })
        }
        options={[
          /*
            Phrased as work outstanding, not as a category. Every ticket is
            meant to have a pipeline; this filter exists to find the ones that
            do not yet, so "No pipeline" read like a legitimate bucket to leave
            things in. Reporting treats it the same way -- see the pipeline
            report, where it is a triage note rather than a slice of the chart.
          */
          { value: "unassigned", label: "Needs pipeline" },
          ...pipelines.map((pipeline) => ({
            value: String(pipeline.id),
            label: pipeline.name,
          })),
        ]}
      />

      {showAssignee ? (
        <Select
          compact={!isDesktop}
          label="Assignee"
          value={filters.assignee ?? ""}
          onChange={(raw) =>
            onChange({
              ...filters,
              assignee:
                raw === ""
                  ? undefined
                  : raw === "unassigned"
                    ? "unassigned"
                    : Number(raw),
            })
          }
          options={[
            { value: "unassigned", label: "Unassigned" },
            ...users.map((user) => ({
              value: String(user.id),
              label: user.name,
            })),
          ]}
        />
      ) : null}
      </div>

      {/*
        Said out loud, because it contradicts the view the agent is looking at:
        "All open tickets" showing a closed one is a bug unless it is explained.
      */}
      {searchSpansClosed ? (
        <span className="text-xs text-muted">
          Searching all tickets, including closed
        </span>
      ) : null}

      {active > 0 ? (
        <button
          type="button"
          onClick={() => onChange({})}
          className="rounded-md px-2 py-1 text-xs text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
        >
          Clear {active} filter{active === 1 ? "" : "s"}
        </button>
      ) : null}
    </div>
  );
}

interface SelectOption {
  value: string;
  label: string;
}

/** Options under an `<optgroup>`, or loose when there is no label. */
interface SelectGroup {
  label?: string;
  options: SelectOption[];
}

function Select({
  label,
  value,
  onChange,
  options = [],
  groups = [],
  compact = false,
}: {
  label: string;
  /** Phone: no visible label, the empty option names the filter instead. */
  compact?: boolean;
  value: string | number;
  onChange: (value: string) => void;
  options?: SelectOption[];
  groups?: SelectGroup[];
}) {
  const isSet = value !== "";

  return (
    <span className={cn("items-center", compact ? "flex min-w-0 flex-1" : "inline-flex")}>
      <label
        className={cn(
          "items-center gap-1.5 text-xs text-muted",
          compact ? "relative flex min-w-0 flex-1" : "inline-flex",
        )}
      >
        <span className={cn(compact && "sr-only")}>{label}</span>
        {/*
          Our own chevron on a phone. The browser's sits in a gutter wide
          enough to cost "Assignee" its last letters on a 375px screen, where
          three of these share what the account avatar leaves of the row.
        */}
        {compact ? (
          <span
            aria-hidden
            className="pointer-events-none absolute right-1 text-xs text-muted"
          >
            {"\u25be"}
          </span>
        ) : null}
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={cn(
            "border pl-2 text-xs",
            !compact && "pr-2",
            // A phone gets the taller box: it is tapped, not clicked.
            compact
              ? "h-8 w-full min-w-0 appearance-none pr-4"
              : "h-7",
            isSet ? "rounded-l-md" : "rounded-md",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            // An applied filter is visibly different, so nobody wonders why the
            // list is short.
            isSet
              ? "border-brand-500 bg-brand-50 text-content dark:bg-brand-950/40"
              : "border-line bg-surface text-content",
          )}
        >
          <option value="">{compact ? label : "Any"}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {groups.map((group, index) =>
            group.label ? (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ) : (
              group.options.map((option) => (
                <option key={`${index}:${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))
            ),
          )}
        </select>
      </label>

      {/*
        Clearing one filter without losing the rest. "Any" in the dropdown does
        the same job, but it is two clicks and a scan of the list to find it --
        and with several filters applied the only obvious control was the one
        that cleared the lot.
      */}
      {isSet ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={`Clear ${label.toLowerCase()} filter`}
          title={`Clear ${label.toLowerCase()} filter`}
          className={cn(
            "-ml-px flex shrink-0 items-center justify-center rounded-r-md border border-l-0 text-xs",
            compact ? "h-8 w-7" : "h-7 w-5",
            "border-brand-500 bg-brand-50 text-muted hover:text-brand-600 dark:hover:text-brand-400 dark:bg-brand-950/40",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        >
          &times;
        </button>
      ) : null}
    </span>
  );
}
